import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo, useState } from 'react';
import { Search, Play, Check, Folder, Folders, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ComparedVideo, formatTime } from './videoDuelData';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videos: ComparedVideo[];
  selectedIds: string[];
  excludeId: string | null;
  onSelect: (id: string) => void;
  slot: 'A' | 'B' | 'C';
}

export function VideoComparisonModal({ open, onOpenChange, videos, selectedIds, excludeId, onSelect, slot }: Props) {
  useLanguage();
  const [search, setSearch] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  // Pastas raiz: carrega 1 vez, cache permanente. Próximas aberturas do modal
  // reutilizam o cache do React Query (staleTime + gcTime infinitos).
  const { data: foldersData } = useQuery({
    queryKey: ['video-duel-folders'],
    queryFn: () => hierarchicalFoldersService.listRoot(),
    enabled: open,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Vídeos da pasta selecionada: cache por folderId.
  const { data: folderDetails, isLoading: isFolderLoading } = useQuery({
    queryKey: ['video-duel-folder-details', currentFolderId],
    queryFn: () => hierarchicalFoldersService.getFolderDetails(currentFolderId!),
    enabled: open && !!currentFolderId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const rootFolders = foldersData?.items || [];

  // Lista efetiva de vídeos a exibir, conforme contexto: pasta atual ou todos.
  const displayVideos: ComparedVideo[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (currentFolderId) {
      const folderVideos: ComparedVideo[] = (folderDetails?.videos || []).map((v: any) => ({
        id: v.id,
        name: v.title,
        thumbnailUrl: v.thumbnailUrl || '',
        durationInSeconds: Math.round(v.duration || 0),
      }));
      if (!q) return folderVideos;
      return folderVideos.filter((v) => v.name.toLowerCase().includes(q));
    }
    if (!q) return videos;
    return videos.filter((v) => v.name.toLowerCase().includes(q));
  }, [currentFolderId, folderDetails, videos, search]);

  const handleSelect = (id: string) => {
    onSelect(id);
    onOpenChange(false);
    setSearch('');
    setCurrentFolderId(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearch('');
      setCurrentFolderId(null);
    }
    onOpenChange(next);
  };

  const sectionTitle = currentFolderId
    ? folderDetails?.name || 'Pasta'
    : translateInterfaceText("Todos os vídeos");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[95vw] sm:w-[80%] md:w-[65%] lg:w-[50%] max-w-[820px] sm:max-w-[820px] h-[85vh] sm:h-[75vh] lg:h-[70vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-[hsl(var(--border))]">
          <DialogTitle className="text-lg font-bold text-[hsl(var(--foreground))]">
            {translateInterfaceText("Selecionar vídeo para comparação")}</DialogTitle>
          <p className="text-[13px] text-[hsl(var(--muted-foreground))] mt-1">
            {translateInterfaceText("Selecione o vídeo que deseja adicionar ao slot")}{" "}{slot}.
          </p>

          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <Input
              placeholder={translateInterfaceText("Pesquisar vídeos")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 bg-[hsl(var(--muted))/0.3] border-[hsl(var(--border))]"
            />
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Sidebar — pastas reais do usuário */}
          {rootFolders.length > 0 && (
            <div className="w-[200px] shrink-0 border-r border-[hsl(var(--border))] py-3 flex flex-col min-h-0">
              <ScrollArea className="flex-1 min-h-0">
                <div className="space-y-0.5 px-2">
                  <button
                    onClick={() => setCurrentFolderId(null)}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors',
                      !currentFolderId
                        ? 'text-[#2563EB] font-semibold bg-[hsl(var(--muted))/0.5]'
                        : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))/0.5]',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <Folders className="h-4 w-4" />
                      {translateInterfaceText("Todos os vídeos")}</span>
                    <span
                      className={cn(
                        'text-[11px] min-w-[20px] h-5 flex items-center justify-center rounded-full',
                        !currentFolderId
                          ? 'text-[#2563EB] bg-[#EAF1FF]'
                          : 'text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]',
                      )}
                    >
                      {videos.length}
                    </span>
                  </button>
                  {rootFolders.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setCurrentFolderId(f.id)}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors',
                        currentFolderId === f.id
                          ? 'text-[#2563EB] font-semibold bg-[hsl(var(--muted))/0.5]'
                          : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))/0.5]',
                      )}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Folder className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{f.name}</span>
                      </span>
                      <span
                        className={cn(
                          'text-[11px] min-w-[20px] h-5 flex items-center justify-center rounded-full flex-shrink-0',
                          currentFolderId === f.id
                            ? 'text-[#2563EB] bg-[#EAF1FF]'
                            : 'text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]',
                        )}
                      >
                        {f.videosCount || 0}
                      </span>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Video grid */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="px-5 pt-3 pb-1 flex-shrink-0">
              <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">{sectionTitle}</h3>
            </div>

            {/* Breadcrumb — montado a partir de folderDetails.breadcrumb (suporta subpastas) */}
            {currentFolderId && folderDetails && (
              <div className="px-5 pb-2 flex-shrink-0">
                <nav className="flex items-center gap-1.5 text-[12px] flex-wrap">
                  <button
                    type="button"
                    onClick={() => setCurrentFolderId(null)}
                    className="text-[#2563EB] hover:text-[#1d4ed8] font-medium transition-colors"
                  >
                    {translateInterfaceText("Todos os vídeos")}</button>
                  {folderDetails.breadcrumb.map((item) => (
                    <React.Fragment key={item.id}>
                      <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
                      <button
                        type="button"
                        onClick={() => setCurrentFolderId(item.id)}
                        className="text-[#2563EB] hover:text-[#1d4ed8] font-medium transition-colors truncate max-w-[140px]"
                      >
                        {item.name}
                      </button>
                    </React.Fragment>
                  ))}
                  <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
                  <span className="text-[hsl(var(--foreground))] font-semibold truncate max-w-[160px]">
                    {folderDetails.name}
                  </span>
                </nav>
              </div>
            )}

            <ScrollArea className="flex-1 min-h-0">
              {/* Subpastas — exibidas acima dos vídeos quando a pasta atual tem filhas */}
              {currentFolderId && !search.trim() && folderDetails?.children && folderDetails.children.length > 0 && (
                <div className="px-5 pt-2 pb-1">
                  <p className="text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">{translateInterfaceText("Subpastas")}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {folderDetails.children.map(child => (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => setCurrentFolderId(child.id)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-[10px] border border-[hsl(var(--border))] hover:border-[#4B7BFF]/50 hover:bg-[#EAF1FF] transition-colors text-left"
                      >
                        <Folder className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
                        <span className="text-[13px] truncate flex-1 text-[hsl(var(--foreground))]">{child.name}</span>
                        <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex-shrink-0">{child.videosCount}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {currentFolderId && isFolderLoading ? (
                <div className="grid grid-cols-2 gap-3 p-5 pt-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-[120px] rounded-xl bg-[hsl(var(--muted))]/50 animate-pulse" />
                  ))}
                </div>
              ) : displayVideos.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-sm text-[hsl(var(--muted-foreground))]">
                  {translateInterfaceText("Nenhum vídeo encontrado")}</div>
              ) : (
                <div className="grid grid-cols-2 gap-3 p-5 pt-2">
                  {displayVideos.map((v) => {
                    const isAlreadySelected = selectedIds.includes(v.id) && v.id !== excludeId;
                    return (
                      <button
                        key={v.id}
                        onClick={() => !isAlreadySelected && handleSelect(v.id)}
                        disabled={isAlreadySelected}
                        className={cn(
                          'group text-left rounded-xl border overflow-hidden transition-colors bg-[hsl(var(--card))]',
                          isAlreadySelected
                            ? 'border-[hsl(var(--border))] opacity-50 cursor-not-allowed'
                            : 'border-[hsl(var(--border))] hover:border-[#4B7BFF]/40 cursor-pointer',
                        )}
                      >
                        <div className="relative aspect-video bg-[hsl(var(--muted))] overflow-hidden">
                          {v.thumbnailUrl ? (
                            <img
                              src={v.thumbnailUrl}
                              alt={v.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-8 w-8 text-[hsl(var(--muted-foreground))/0.3]" />
                            </div>
                          )}
                          <div className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-sm text-white px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums">
                            {formatTime(v.durationInSeconds)}
                          </div>
                          {isAlreadySelected && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <div className="h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg">
                                <Check className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <h4 className="text-[13px] font-semibold text-[hsl(var(--foreground))] truncate mb-0.5">
                            {v.name}
                          </h4>
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                              {formatTime(v.durationInSeconds)}
                            </span>
                            {isAlreadySelected && (
                              <span className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-0.5 rounded-full">
                                {translateInterfaceText("Em uso")}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
